// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
import { Paper } from '@mui/material';
import { ReactNode } from 'react';

// How far the tooltip sits from the cursor, and how close to the viewport edge
// it may come before it flips to the other side.
const TOOLTIP_OFFSET = 14;
const TOOLTIP_WIDTH = 220;

interface Props {
  x: number;
  y: number;
  testId: string;
  children: ReactNode;
}

/**
 * A tooltip pinned to the cursor rather than to an element.
 *
 * A window runs for years, so one tooltip per bar would mount thousands of them
 * to show one. Both charts place a single one from the pointer's own
 * coordinates instead.
 */
export function ChartCursorTooltip(props: Props) {
  // Fixed rather than absolute, so no offset parent has to be measured. It
  // flips left near the viewport edge.
  const flipsLeft =
    props.x + TOOLTIP_OFFSET + TOOLTIP_WIDTH > window.innerWidth;

  return (
    <Paper
      elevation={4}
      data-testid={props.testId}
      sx={{
        position: 'fixed',
        zIndex: (theme) => theme.zIndex.tooltip,
        pointerEvents: 'none',
        width: TOOLTIP_WIDTH,
        p: 1,
        left: flipsLeft ? undefined : props.x + TOOLTIP_OFFSET,
        right: flipsLeft
          ? window.innerWidth - props.x + TOOLTIP_OFFSET
          : undefined,
        top: props.y + TOOLTIP_OFFSET,
      }}
    >
      {props.children}
    </Paper>
  );
}
